import { useMutation, useQuery } from '@tanstack/react-query';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';

import API from '@api/API';

import {
  EntertainerFilter,
  useCurrentEntertainerQuery,
  useCurrentEntertainerScheduleQuery,
  useFilteredEntertainersQuery,
} from '@queries/EntertainerQueries';

import { AddEntertainerMediaPayload } from '@mutations/EntertainerMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCityById } from '@hooks/useCurrentUserLocation';

import { useNavigate } from '@router/index';

import Button from '@components/buttons/Button';
import { FormLabel } from '@components/form/FormLabel';
import { Textarea } from '@components/form/Textarea';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { PrimaryCtaCard } from '@components/layout/PrimaryCtaCard/PrimaryCtaCard';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { EntertainerSpotlightSection } from '@screens/series/SeriesFeatureFormScreen/SeriesFeatureFormScreen';

import stageIconUrl from '../../CommunityScreen/assets/stage.svg';
import {
  AddImageContent,
  AddImageContentSubmitPayload,
} from '../components/FeaturedPhotoManagementSection';
import { InstagramHandlePopup } from './InstagramHandlePopup';

interface BookDragKingsApplication {
  _id: string;
  entertainerMediaId?: string;
  questions: Record<
    string,
    {
      question: string;
      answer: string;
      meta?: Record<string, any>;
    }
  >;
}

const useBookDragKingsApplicationQuery = ({ instagramHandle }: { instagramHandle?: string }) => {
  const { data, isFetched, refetch } = useQuery({
    queryKey: ['bookDragKings', 'application', instagramHandle ?? 'missing'],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (instagramHandle) {
        queryParams.set('instagramHandle', instagramHandle);
      }
      const response = await API.get<{ bookDragKingsApplication?: BookDragKingsApplication }>(
        `/v1/book-drag-kings/application?${queryParams.toString()}`,
      );
      return response.bookDragKingsApplication;
    },
    enabled: Boolean(instagramHandle),
  });

  return {
    bookDragKingsApplication: data,
    isBookDragKingsApplicationReady: isFetched,
    refetchBookDragKingsApplication: refetch,
  };
};

const useUpdateBookDragKingsApplicationMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: (
      updatedProperties: { instagramHandle: string } & Partial<{
        entertainerMedia?: AddEntertainerMediaPayload;
        questions: Record<
          string,
          {
            question: string;
            answer: string;
            meta?: Record<string, any>;
          }
        >;
      }>,
    ) => {
      return API.post(`/v1/book-drag-kings/application`, updatedProperties);
    },
  });

  return {
    isUpdatingBookDragKingsApplication: isPending,
    updateBookDragKingsApplicationAsync: mutateAsync,
  };
};

enum FeatureFormStep {
  AddLook = 'addLook',
  BookingKings = 'bookingKings',
  Story = 'story',
  Shoutouts = 'shoutouts',
}

interface FeatureFormStepProps {
  stepLabel: (title: string) => string;
  isFirstStep: boolean;
  bookDragKingsApplication?: BookDragKingsApplication;
  instagramHandle?: string;
  onSave: () => void;
  onCancel: () => void;
}

const AddLookStep: FC<FeatureFormStepProps & { cityId: string }> = ({
  stepLabel,
  cityId,
  instagramHandle,
  onSave,
  onCancel,
}) => {
  const { updateBookDragKingsApplicationAsync } = useUpdateBookDragKingsApplicationMutation();

  const handleSubmit = async (payload: AddImageContentSubmitPayload) => {
    // goToNextStep prompts for the Instagram handle instead of advancing
    if (!instagramHandle) {
      onSave();
      return;
    }

    await updateBookDragKingsApplicationAsync({
      instagramHandle,
      entertainerMedia: payload,
    });
    onSave();
  };

  return (
    <>
      <TitleToolbar size="md" text={stepLabel('Add Your Look')} className="mb-0" />

      <p className="mt-1">
        You may have seen the featured drag kings on @bookdragkings. If you want to be featured on
        our Instagram, select a photo and name your look!
      </p>

      <AddImageContent
        cityId={cityId}
        onSubmit={handleSubmit}
        onSave={onSave}
        entertainerCategories={['Drag King']}
        meta={{
          channel: 'dragkingcommunity',
          channelAction: 'ayrshare',
        }}
      />

      <Button
        className="mt-2"
        roundness="rounded"
        variant="flat"
        color="neutral"
        onClick={onCancel}
      >
        Cancel
      </Button>
    </>
  );
};

const QuestionsStep: FC<{
  title: string;
  description: ReactNode;
  questions: Record<string, { question: string; placeholder: string }>;
  bookDragKingsApplication?: BookDragKingsApplication;
  instagramHandle?: string;
  trackEvent: string;
  onSave: () => void;
}> = ({
  title,
  description,
  questions,
  bookDragKingsApplication,
  instagramHandle,
  trackEvent,
  onSave,
}) => {
  const { track } = useAnalytics();
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.keys(questions).map((key) => [
        key,
        bookDragKingsApplication?.questions?.[key]?.answer ?? '',
      ]),
    ),
  );

  const { isUpdatingBookDragKingsApplication, updateBookDragKingsApplicationAsync } =
    useUpdateBookDragKingsApplicationMutation();

  const handleContinue = async () => {
    // goToNextStep prompts for the Instagram handle instead of advancing
    if (!instagramHandle) {
      onSave();
      return;
    }

    track(trackEvent, { answers });
    await updateBookDragKingsApplicationAsync({
      instagramHandle,
      questions: Object.fromEntries(
        Object.entries(questions).map(([key, { question }]) => [
          key,
          { question, answer: answers[key] },
        ]),
      ),
    });
    onSave();
  };

  const isMissingAnswers = Object.keys(questions).some((key) => !answers[key]?.trim());

  return (
    <>
      <TitleToolbar size="md" text={title} className="mb-0" />

      <p className="mt-1">{description}</p>

      <div className="d-grid gap-2">
        {Object.entries(questions).map(([key, { question, placeholder }]) => (
          <div key={key}>
            <FormLabel text={question} />
            <Textarea
              name={key}
              placeholder={placeholder}
              defaultValue={answers[key]}
              onChange={(updatedValue) => {
                setAnswers((prevAnswers) => ({ ...prevAnswers, [key]: updatedValue }));
              }}
            />
          </div>
        ))}
        <Button
          roundness="rounded"
          disabled={isMissingAnswers || isUpdatingBookDragKingsApplication}
          onClick={handleContinue}
        >
          {isUpdatingBookDragKingsApplication ? 'Submitting...' : 'Continue'}
        </Button>
      </div>
    </>
  );
};

const BookingKingsStep: FC<FeatureFormStepProps> = ({
  stepLabel,
  bookDragKingsApplication,
  instagramHandle,
  onSave,
}) => {
  return (
    <QuestionsStep
      bookDragKingsApplication={bookDragKingsApplication}
      instagramHandle={instagramHandle}
      title={stepLabel('Why Book Drag Kings?')}
      description="Help us make the case for drag kings! Your answers may be shared to encourage producers and the community to support more kings & things."
      questions={{
        whyBookKings: {
          question: 'Why do you think producers should book more drag kings? *',
          placeholder: 'Producers should book more drag kings because...',
        },
        aboutKingsForCommunity: {
          question: 'What do you think the LGBTQ+ community needs to know about drag kings? *',
          placeholder: 'I wish the community knew...',
        },
      }}
      trackEvent="Submit Drag King Feature Form Booking Kings"
      onSave={onSave}
    />
  );
};

const StoryStep: FC<FeatureFormStepProps & { cityId: string }> = ({
  stepLabel,
  cityId,
  bookDragKingsApplication,
  instagramHandle,
  onSave,
}) => {
  const { city } = useCityById(cityId);
  const cityName = city?.name ?? 'your city';

  return (
    <QuestionsStep
      bookDragKingsApplication={bookDragKingsApplication}
      instagramHandle={instagramHandle}
      title={stepLabel('Share Your Story')}
      description={`We want to properly highlight of your drag and what makes you unique! Answer some questions about yourself and the local drag scene in ${cityName}.`}
      questions={{
        aboutEntertainer: {
          question: 'About your drag *',
          placeholder: 'Tell us about your drag...',
        },
        aboutScene: {
          question: `What do you wish vistors knew about drag in ${cityName}? *`,
          placeholder: `You HAVE to see drag in ${cityName} because...`,
        },
      }}
      trackEvent="Submit Drag King Feature Form Story"
      onSave={onSave}
    />
  );
};

const SPOTLIGHT_QUESTIONS = [
  { key: 'spotlightOne', question: 'First entertainer spotlight' },
  { key: 'spotlightTwo', question: 'Second entertainer spotlight' },
];

const ShoutoutsStep: FC<FeatureFormStepProps> = ({
  stepLabel,
  isFirstStep,
  bookDragKingsApplication,
  instagramHandle,
  onSave,
  onCancel,
}) => {
  const { track } = useAnalytics();
  const { isUpdatingBookDragKingsApplication, updateBookDragKingsApplicationAsync } =
    useUpdateBookDragKingsApplicationMutation();
  const { filteredEntertainers } = useFilteredEntertainersQuery({
    type: 'dragPerformer',
    filter: EntertainerFilter.DragKingsThings,
  });
  const entertainerOptions: { value: string; label: string }[] = useMemo(() => {
    return filteredEntertainers.map(({ _id, title }) => ({ value: _id, label: title }));
  }, [filteredEntertainers]);

  const [spotlights, setSpotlights] = useState<
    { entertainerId: string; entertainerTitle?: string; reason: string }[]
  >(() =>
    SPOTLIGHT_QUESTIONS.map(({ key }) => {
      const meta = bookDragKingsApplication?.questions?.[key]?.meta;
      return {
        entertainerId: meta?.entertainerId ?? '',
        entertainerTitle: meta?.entertainerTitle,
        reason: meta?.reason ?? '',
      };
    }),
  );

  const updateSpotlightEntertainer =
    (index: number) =>
    (
      updatedEntertainer:
        | { entertainerId: string; entertainerTitle?: undefined }
        | { entertainerId?: undefined; entertainerTitle: string },
    ) => {
      setSpotlights((prevValue) => {
        const updatedValue = [...prevValue];
        if (updatedEntertainer.entertainerId) {
          updatedValue[index].entertainerId = updatedEntertainer.entertainerId;
          updatedValue[index].entertainerTitle = undefined;
        } else {
          updatedValue[index].entertainerId = undefined;
          updatedValue[index].entertainerTitle = updatedEntertainer.entertainerTitle;
        }
        return updatedValue;
      });
    };

  const updateSpotlightEntertainerReason = (index: number) => (updatedReason: string) => {
    setSpotlights((prevValue) => {
      const updatedValue = [...prevValue];
      updatedValue[index].reason = updatedReason;
      return updatedValue;
    });
  };

  const hasEmptyFields =
    spotlights.length &&
    spotlights.some(
      ({ entertainerId, entertainerTitle, reason }) =>
        (!entertainerId && !entertainerTitle) || !reason,
    );
  const isSameSpotlightEntertainer =
    spotlights?.[0]?.entertainerId &&
    spotlights?.[0]?.entertainerId === spotlights?.[1]?.entertainerId;

  const handleSubmit = async () => {
    if (!instagramHandle) {
      return;
    }

    track('Submit Drag King Thing Shoutouts', spotlights);

    await updateBookDragKingsApplicationAsync({
      instagramHandle,
      questions: Object.fromEntries(
        SPOTLIGHT_QUESTIONS.map(({ key, question }, index) => {
          const { entertainerId, entertainerTitle, reason } = spotlights[index];
          const entertainer = entertainerOptions.find(({ value }) => value === entertainerId);

          return [
            key,
            {
              question,
              answer: entertainer?.label ?? entertainerTitle ?? '',
              meta: { entertainerId, entertainerTitle, reason },
            },
          ];
        }),
      ),
    });

    onSave();
  };

  return (
    <>
      <TitleToolbar size="md" text={stepLabel('Support Other Kings & Things')} className="mb-0" />
      {isFirstStep ? (
        <p className="mt-1">
          Thank you for wanting to support other kings! Please complete the form below to submit
          your shoutouts!
        </p>
      ) : (
        <p className="mt-1">
          Thank you for submitting your look! Before we do your feature, complete your application
          by supporting some other kings & things using the form below.
        </p>
      )}

      <div className="d-grid gap-2">
        <EntertainerSpotlightSection
          label="First entertainer *"
          entertainerOptions={entertainerOptions}
          spotlights={spotlights}
          index={0}
          name="spotlightOne"
          updateSpotlightEntertainer={updateSpotlightEntertainer}
          updateSpotlightEntertainerReason={updateSpotlightEntertainerReason}
        />
        {(spotlights?.[0]?.entertainerId || spotlights?.[0]?.entertainerTitle) && (
          <EntertainerSpotlightSection
            label="Second entertainer *"
            entertainerOptions={entertainerOptions}
            spotlights={spotlights}
            index={1}
            name="spotlightTwo"
            updateSpotlightEntertainer={updateSpotlightEntertainer}
            updateSpotlightEntertainerReason={updateSpotlightEntertainerReason}
          />
        )}
        <Button
          data-testid="stepthree-submit-button"
          roundness="rounded"
          disabled={
            hasEmptyFields ||
            isSameSpotlightEntertainer ||
            !instagramHandle ||
            isUpdatingBookDragKingsApplication
          }
          onClick={handleSubmit}
        >
          {isUpdatingBookDragKingsApplication ? 'Submitting...' : 'Finish up!'}
        </Button>
        {isFirstStep && (
          <Button roundness="rounded" variant="flat" color="neutral" onClick={onCancel}>
            Cancel
          </Button>
        )}
        {isSameSpotlightEntertainer && (
          <p className="text-danger mt-0 text-center">Please select two different entertainers.</p>
        )}
      </div>
    </>
  );
};

const FeatureFormComplete: FC<{
  backUrl: string;
  onCancel: () => void;
  onAddVideoClick: () => void;
}> = ({ backUrl, onCancel, onAddVideoClick }) => {
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const { schedule } = useCurrentEntertainerScheduleQuery();
  const navigate = useNavigate();

  const videoCount =
    currentEntertainer?.featuredMedia?.filter((media: any) => media.type === 'video')?.length ?? 0;

  return (
    <>
      <div className="mb-3">
        <PrimaryCtaCard
          cta={{
            img: stageIconUrl,
            title: `Let's see you in action`,
            description: `Send a clip and show us what you're like on stage.`,
            url: '',
            badgeCount: videoCount ? undefined : 1,
            onClick: onAddVideoClick,
          }}
          track={{
            event: `Add video`,
          }}
        />
      </div>
      {!schedule?.didSubmitThisWeek && (
        <EmptyState
          title="Application Submitted!"
          text="Thank you for applying to be featured! We will review your application. In the meantime, update your schedule so we can share your upcoming shows too!"
          button={{
            text: 'Update your schedule',
            onClick: () => {
              navigate(`/manage/entertainer/schedule`, {
                state: {
                  back: backUrl,
                },
              });
            },
          }}
          secondaryButton={{
            className: '',
            text: 'Return to directory',
            variant: 'flat',
            onClick: onCancel,
          }}
        />
      )}
    </>
  );
};

export const FeatureForm: FC<{
  didSubmitImage: boolean;
  instagramHandle?: string;
  cityId: string;
  backUrl: string;
  onCancel: () => void;
  onAddVideoClick: () => void;
}> = ({
  instagramHandle: initialInstagramHandle,
  didSubmitImage,
  cityId,
  backUrl,
  onCancel,
  onAddVideoClick,
}) => {
  const { track } = useAnalytics();
  const [isInstagramHandlePopupVisible, setIsInstagramHandlePopupVisible] =
    useState<boolean>(!initialInstagramHandle);
  const [instagramHandle, setInstagramHandle] = useState<string | undefined>(
    initialInstagramHandle,
  );
  const { bookDragKingsApplication, isBookDragKingsApplicationReady } =
    useBookDragKingsApplicationQuery({ instagramHandle });

  const [steps] = useState<FeatureFormStep[]>(() => [
    FeatureFormStep.AddLook,
    FeatureFormStep.BookingKings,
    FeatureFormStep.Story,
    FeatureFormStep.Shoutouts,
  ]);
  const [stepIndex, setStepIndex] = useState(didSubmitImage ? 1 : 0);
  const [isComplete, setIsComplete] = useState(false);
  const step = steps[stepIndex];

  useEffect(() => {
    track('Show Drag King Feature Form Step', { step });
  }, [step]);

  const goToNextStep = () => {
    if (!instagramHandle) {
      setIsInstagramHandlePopupVisible(true);
      return;
    }

    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      setIsComplete(true);
    }
  };

  const stepProps: FeatureFormStepProps = {
    stepLabel: (title) => (steps.length > 1 ? `Step ${stepIndex + 1}: ${title}` : title),
    isFirstStep: stepIndex === 0,
    bookDragKingsApplication,
    instagramHandle,
    onSave: goToNextStep,
    onCancel,
  };

  const renderContent = () => {
    if (isComplete) {
      return (
        <FeatureFormComplete
          backUrl={backUrl}
          onCancel={onCancel}
          onAddVideoClick={onAddVideoClick}
        />
      );
    }

    // The application query only runs once an Instagram handle has been entered
    if (instagramHandle && !isBookDragKingsApplicationReady) {
      return <Skeleton height={200} />;
    }

    return (
      <>
        {step === FeatureFormStep.AddLook && <AddLookStep {...stepProps} cityId={cityId} />}
        {step === FeatureFormStep.BookingKings && <BookingKingsStep {...stepProps} />}
        {step === FeatureFormStep.Story && <StoryStep {...stepProps} cityId={cityId} />}
        {step === FeatureFormStep.Shoutouts && <ShoutoutsStep {...stepProps} />}
      </>
    );
  };

  return (
    <>
      {renderContent()}
      {/* Framework7 moves the popup element to the app root while it's open, so keep it mounted
          in its own wrapper; otherwise React throws when the content next to it changes */}
      <div>
        <InstagramHandlePopup
          isVisible={isInstagramHandlePopupVisible}
          onClose={() => setIsInstagramHandlePopupVisible(false)}
          onSubmit={setInstagramHandle}
        />
      </div>
    </>
  );
};
